<template>
  <div class="chat-chart-block my-3 rounded-xl border border-slate-600/50 bg-slate-950/40 p-3 overflow-x-auto">
    <p v-if="error" class="text-xs text-amber-300/90">{{ error }}</p>
    <div v-else class="relative h-56 w-full min-w-[240px] max-w-full">
      <canvas :id="canvasId" ref="canvas" />
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto'

const ALLOWED_TYPES = new Set(['bar', 'line', 'pie', 'doughnut'])
const MAX_LABELS = 80
const MAX_DATASETS = 12
const MAX_POINTS = 200

const DEFAULT_COLORS = [
  'rgba(34, 211, 238, 0.85)',
  'rgba(129, 140, 248, 0.9)',
  'rgba(52, 211, 153, 0.9)',
  'rgba(251, 191, 36, 0.9)',
  'rgba(248, 113, 113, 0.85)',
  'rgba(167, 139, 250, 0.9)',
  'rgba(94, 234, 212, 0.85)',
  'rgba(251, 146, 60, 0.9)'
]

function normalizeSpec(raw) {
  if (!raw || typeof raw !== 'object') return null
  const type = String(raw.type || 'bar').toLowerCase()
  if (!ALLOWED_TYPES.has(type)) return null

  const data = raw.data
  if (!data || typeof data !== 'object') return null

  let labels = Array.isArray(data.labels) ? data.labels.map((x) => String(x)) : []
  if (labels.length > MAX_LABELS) labels = labels.slice(0, MAX_LABELS)

  let datasetsIn = Array.isArray(data.datasets) ? data.datasets : []
  if (datasetsIn.length > MAX_DATASETS) datasetsIn = datasetsIn.slice(0, MAX_DATASETS)

  const datasets = datasetsIn.map((ds, i) => {
    const label = ds && ds.label != null ? String(ds.label) : `Série ${i + 1}`
    let arr = Array.isArray(ds?.data) ? ds.data.map((n) => Number(n)) : []
    if (arr.length > MAX_POINTS) arr = arr.slice(0, MAX_POINTS)
    const color = DEFAULT_COLORS[i % DEFAULT_COLORS.length]
    const base = {
      label,
      data: arr
    }
    if (type === 'pie' || type === 'doughnut') {
      return {
        ...base,
        backgroundColor: arr.map((_, j) => DEFAULT_COLORS[j % DEFAULT_COLORS.length]),
        borderColor: 'rgba(15, 23, 42, 0.9)',
        borderWidth: 1
      }
    }
    return {
      ...base,
      backgroundColor: type === 'line' ? color.replace('0.85', '0.25').replace('0.9', '0.25') : color,
      borderColor: color,
      borderWidth: type === 'line' ? 2 : 1,
      tension: 0.25,
      fill: type === 'line' ? false : undefined
    }
  })

  if (!datasets.length) return null

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: { color: '#cbd5e1', font: { size: 11 } }
      }
    },
    ...(typeof raw.options === 'object' && raw.options ? raw.options : {})
  }

  if (!options.plugins) options.plugins = {}
  if (!options.plugins.legend) options.plugins.legend = {}
  if (!options.plugins.legend.labels) options.plugins.legend.labels = {}
  options.plugins.legend.labels.color = '#cbd5e1'

  if (type === 'bar' || type === 'line') {
    options.scales = options.scales || {}
    const axisStyle = {
      ticks: { color: '#94a3b8', maxTicksLimit: 12 },
      grid: { color: 'rgba(148, 163, 184, 0.12)' }
    }
    options.scales.x = { ...axisStyle, ...(options.scales.x || {}) }
    options.scales.y = { ...axisStyle, ...(options.scales.y || {}) }
  }

  return { type, data: { labels, datasets }, options }
}

export default {
  name: 'ChatChartBlock',

  props: {
    spec: {
      type: Object,
      required: true
    },
    chartKey: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      error: '',
      _chart: null
    }
  },

  computed: {
    canvasId() {
      const s = String(this.chartKey || 'c').replace(/[^a-zA-Z0-9_-]/g, '-')
      return `chat-chart-${s}`
    }
  },

  watch: {
    spec: {
      deep: true,
      handler() {
        this.$nextTick(() => this.renderChart())
      }
    }
  },

  mounted() {
    this.renderChart()
  },

  beforeUnmount() {
    this.destroyChart()
  },

  methods: {
    destroyChart() {
      if (this._chart) {
        this._chart.destroy()
        this._chart = null
      }
    },

    renderChart() {
      this.destroyChart()
      this.error = ''
      const normalized = normalizeSpec(this.spec)
      if (!normalized) {
        this.error = 'Gráfico inválido: use type bar|line|pie|doughnut e data.labels + data.datasets[].data.'
        return
      }
      const canvas = this.$refs.canvas
      if (!canvas) return
      try {
        this._chart = new Chart(canvas.getContext('2d'), normalized)
      } catch {
        this.error = 'Não foi possível desenhar o gráfico.'
      }
    }
  }
}
</script>
